import { useState, useCallback } from 'react';
import { useAccount, useSignMessage, useSwitchChain, useChainId } from 'wagmi';
import { polygon } from 'wagmi/chains';
import { walletApi } from '../api/wallet';

export type LinkOutcome =
  | { ok: true }
  | { ok: false; error: string };

interface LinkResult {
  linking: boolean;
  /** Last error message — kept in state for callers that want to render it. */
  error: string | null;
  /** Run the full (ensure-Polygon →) challenge → sign → link flow. */
  link: () => Promise<LinkOutcome>;
}

/**
 * SIWE-style wallet linking:
 *   0. Ensure the wallet is on Polygon (chain 137). Switch to it, or add it if
 *      the wallet doesn't have it yet. Signing on the wrong chain is the #1
 *      reason the link silently stalls.
 *   1. Request a challenge from backend (server returns a message + nonce)
 *   2. User signs the message with their connected wallet
 *   3. POST message + signature to backend to link wallet to current user
 */
export function useLinkWallet(): LinkResult {
  const { address } = useAccount();
  const chainId = useChainId();
  const { signMessageAsync } = useSignMessage();
  const { switchChainAsync } = useSwitchChain();
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = useCallback(async (): Promise<LinkOutcome> => {
    if (!address) {
      const msg = 'Connect a wallet first';
      setError(msg);
      return { ok: false, error: msg };
    }
    setLinking(true);
    setError(null);
    let step: 'switch-chain' | 'challenge' | 'sign' | 'link' = 'switch-chain';
    try {
      // -- Step 0: make sure we're on Polygon before signing --------------
      if (chainId !== polygon.id) {
        try {
          await switchChainAsync({ chainId: polygon.id });
        } catch (switchErr: unknown) {
          const se = switchErr as { code?: number; message?: string };
          // 4902 = chain not added to the wallet. wagmi's connector usually
          // handles wallet_addEthereumChain automatically, but if the switch
          // still failed, surface a clear, actionable message.
          const notAdded =
            se?.code === 4902 || /unrecognized chain|add.*chain|4902/i.test(se?.message ?? '');
          throw new Error(
            notAdded
              ? 'Please add the Polygon network to your wallet, then try linking again.'
              : 'Please switch your wallet to the Polygon network, then try again.',
          );
        }
      }

      step = 'challenge';
      const challenge = await walletApi.challenge(address);

      step = 'sign';
      const signature = await signMessageAsync({ message: challenge.message });

      step = 'link';
      await walletApi.link({ message: challenge.message, signature });

      return { ok: true };
    } catch (err: unknown) {
      const e = err as { shortMessage?: string; message?: string; name?: string };
      const raw = e.shortMessage ?? e.message ?? 'Linking failed';

      let friendly = raw;
      if (/user rejected|user denied|rejected the request/i.test(raw)) {
        friendly = 'You rejected the request in your wallet.';
      } else if (/timeout|timed out|expired/i.test(raw)) {
        friendly = 'Request timed out. Re-open MetaMask and try again.';
      } else if (/disconnect|not connected/i.test(raw)) {
        friendly = 'Wallet disconnected. Reconnect and try again.';
      }

      // eslint-disable-next-line no-console
      console.error(`[useLinkWallet] failed at step "${step}":`, raw, err);

      const msgWithStep =
        step === 'link' || step === 'switch-chain' ? friendly : `${friendly} (failed at: ${step})`;
      setError(msgWithStep);
      return { ok: false, error: msgWithStep };
    } finally {
      setLinking(false);
    }
  }, [address, chainId, signMessageAsync, switchChainAsync]);

  return { linking, error, link };
}
